import type {
  SplitCostingImportError,
  SplitCostingImportWorkflowResult,
} from "../hooks/useSplitCostingImportInvocation";
import type { SplitCostingImportWarning } from "./computeSplitCostingWarnings";
import type { SplitCostingRow } from "./parseSplitCostingCsv";

export type SplitCostingClusterId =
  | "employee"
  | "path"
  | "percentage"
  | "dates"
  | "other"
  | "overAllocation"
  | "duplicateDestination"
  | "readyForValidation"
  | "passed"
  | "notEvaluated"
  | "committing"
  | "imported"
  | "notConfirmed";

export type SplitCostingClusterKind =
  | "error"
  | "warning"
  | "ready"
  | "passed"
  | "neutral";
export type SplitCostingReportTab = "all" | "errors" | "warnings" | "passed";
export type SplitCostingReportState =
  | "validation"
  | "localReady"
  | "batchNotEvaluated"
  | "committing"
  | "committed"
  | "inconsistent";
export type SplitCostingIssueResolution =
  | "edit"
  | "outsidePortal"
  | "sourceFile"
  | "prerequisite";

export interface SplitCostingClusterConfig {
  id: SplitCostingClusterId;
  kind: SplitCostingClusterKind;
  title: string;
  hint: string;
}

export interface ClusteredSplitCostingRow {
  rowIndex: number;
  fileRow: number;
  employeeCode: string;
  path: string;
  row?: SplitCostingRow;
  clusterId: SplitCostingClusterId;
  errors: SplitCostingImportError[];
  warnings: SplitCostingImportWarning[];
  issueResolution: SplitCostingIssueResolution;
}

export interface SplitCostingClusterGroup {
  config: SplitCostingClusterConfig;
  rows: ClusteredSplitCostingRow[];
}

export interface SplitCostingTabCounts {
  all: number;
  errors: number;
  warnings: number;
  passed: number;
}

const ERROR_ORDER: readonly SplitCostingClusterId[] = [
  "employee",
  "path",
  "percentage",
  "dates",
  "other",
];

const WARNING_ORDER: readonly SplitCostingClusterId[] = [
  "overAllocation",
  "duplicateDestination",
];
const STATUS_ORDER: readonly SplitCostingClusterId[] = [
  "readyForValidation",
  "passed",
  "notEvaluated",
  "committing",
  "imported",
  "notConfirmed",
];

export const SPLIT_COSTING_CLUSTER_CONFIGS: Record<
  SplitCostingClusterId,
  SplitCostingClusterConfig
> = {
  employee: {
    id: "employee",
    kind: "error",
    title: "Employee errors",
    hint: "Check the employee code exists on the selected company.",
  },
  path: {
    id: "path",
    kind: "error",
    title: "Path errors",
    hint: "Check Division, Cost Centre, Department, and any lower path levels.",
  },
  percentage: {
    id: "percentage",
    kind: "error",
    title: "Percentage errors",
    hint: "Check each apportioned percentage.",
  },
  dates: {
    id: "dates",
    kind: "error",
    title: "Date errors",
    hint: "Check start and end dates.",
  },
  other: {
    id: "other",
    kind: "error",
    title: "Other validation errors",
    hint: "Review the full message for each row.",
  },
  overAllocation: {
    id: "overAllocation",
    kind: "warning",
    title: "Total percentage exceeds 100%",
    hint: "Review the full allocation for each affected employee.",
  },
  duplicateDestination: {
    id: "duplicateDestination",
    kind: "warning",
    title: "Overlapping rows share a split destination",
    hint: "Review the destination and date range before importing.",
  },
  readyForValidation: {
    id: "readyForValidation",
    kind: "ready",
    title: "Ready for validation",
    hint: "Passed local checks; server validation required.",
  },
  passed: {
    id: "passed",
    kind: "passed",
    title: "Passed",
    hint: "",
  },
  notEvaluated: {
    id: "notEvaluated",
    kind: "neutral",
    title: "Not evaluated",
    hint: "Complete-batch validation has not been confirmed for these rows.",
  },
  committing: {
    id: "committing",
    kind: "neutral",
    title: "Import in progress",
    hint: "The all-or-nothing batch result has not completed yet.",
  },
  imported: {
    id: "imported",
    kind: "passed",
    title: "Imported",
    hint: "These rows were imported as part of the completed batch.",
  },
  notConfirmed: {
    id: "notConfirmed",
    kind: "neutral",
    title: "Import not confirmed",
    hint: "Do not assume these rows were imported; verify in Christmas.",
  },
};

function pathLabel(row: SplitCostingRow | undefined): string {
  if (!row || row.path.length === 0) return "—";
  return row.path.map((part) => part.code).join(" / ");
}

function errorCluster(error: SplitCostingImportError): SplitCostingClusterId {
  const field = error.field?.toLowerCase() ?? "";
  const message = error.error.toLowerCase();
  if (field.includes("employee") || /\bemployee\b/.test(message)) {
    return "employee";
  }
  if (
    field.includes("path") ||
    field.includes("division") ||
    field.includes("costcentre") ||
    field.includes("department") ||
    /\b(path|division|cost centre|department|cost structure)\b/.test(message)
  ) {
    return "path";
  }
  if (
    field.includes("percentage") ||
    /\b(percent|percentage|allocation)\b/.test(message)
  ) {
    return "percentage";
  }
  if (field.includes("date") || /\bdate\b/.test(message)) {
    return "dates";
  }
  return "other";
}

function primaryErrorCluster(
  errors: readonly SplitCostingImportError[]
): SplitCostingClusterId {
  const clusters = new Set(errors.map(errorCluster));
  return ERROR_ORDER.find((id) => clusters.has(id)) ?? "other";
}

function warningCluster(
  warnings: readonly SplitCostingImportWarning[]
): SplitCostingClusterId {
  return warnings.some((warning) => warning.type === "AC-APP-04")
    ? "overAllocation"
    : "duplicateDestination";
}

const NO_EMPLOYEE_COST_STRUCTURE_RE =
  /\b(employee|worker).{0,80}\b(no|without|missing).{0,40}\bcost structure\b|\bno employee cost structure\b/i;

/**
 * Keep action gating and explanatory copy driven by one classification.
 * A row with no parsed backing data is never safe to edit, while AC-ERR-03
 * requires setup in Christmas even though the API can identify its row.
 */
export function classifySplitCostingIssueResolution(
  errors: readonly SplitCostingImportError[],
  row: SplitCostingRow | undefined
): SplitCostingIssueResolution {
  if (errors.some((error) => error.index === null)) return "prerequisite";
  if (!row) return "sourceFile";
  if (errors.some((error) => NO_EMPLOYEE_COST_STRUCTURE_RE.test(error.error))) {
    return "outsidePortal";
  }
  return "edit";
}

export function splitCostingIssueGuidance(
  resolution: SplitCostingIssueResolution
): string {
  if (resolution === "outsidePortal") {
    return "Configure this employee's cost structure in Christmas, then select Validate. This issue cannot be fixed in this portal.";
  }
  if (resolution === "sourceFile") {
    return "Fix this issue in the source CSV and upload it again. No safely parsed row is available to edit in the portal.";
  }
  if (resolution === "prerequisite") {
    return "Configure the company cost structure in Christmas, then select Validate. This prerequisite cannot be fixed in this portal.";
  }
  return "Use Edit in UI to fix this row, then select Validate. You can also fix the source CSV and upload it again.";
}

export interface ClusterSplitCostingRowsInput {
  rows: SplitCostingRow[];
  result: SplitCostingImportWorkflowResult;
  reportState?: SplitCostingReportState;
  warnings?: readonly SplitCostingImportWarning[];
  dismissedWarningIds?: ReadonlySet<string>;
}

function unevaluatedCluster(
  reportState: SplitCostingReportState
): SplitCostingClusterId | null {
  if (reportState === "localReady") return "readyForValidation";
  if (reportState === "batchNotEvaluated") return "notEvaluated";
  if (reportState === "committing") return "committing";
  if (reportState === "committed") return "imported";
  if (reportState === "inconsistent") return "notConfirmed";
  return null;
}

/** Maps every source row to exactly one primary cluster while retaining all messages. */
export function clusterSplitCostingRows({
  rows,
  result,
  reportState = "validation",
  warnings = [],
  dismissedWarningIds = new Set<string>(),
}: ClusterSplitCostingRowsInput): ClusteredSplitCostingRow[] {
  const errorsByIndex = new Map<number, SplitCostingImportError[]>();
  for (const error of result.errors) {
    if (error.index === null) continue;
    const bucket = errorsByIndex.get(error.index);
    if (bucket) bucket.push(error);
    else errorsByIndex.set(error.index, [error]);
  }

  const warningsByFileRow = new Map<number, SplitCostingImportWarning[]>();
  for (const warning of warnings) {
    if (dismissedWarningIds.has(warning.id)) continue;
    for (const fileRow of warning.fileRows) {
      const bucket = warningsByFileRow.get(fileRow);
      if (bucket) bucket.push(warning);
      else warningsByFileRow.set(fileRow, [warning]);
    }
  }

  const indexes = new Set(rows.map((_, index) => index));
  for (const index of errorsByIndex.keys()) indexes.add(index);

  return [...indexes]
    .sort((a, b) => a - b)
    .map((rowIndex) => {
      const row = rows[rowIndex];
      const errors = errorsByIndex.get(rowIndex) ?? [];
      const fileRow = row?.fileRow ?? rowIndex + 2;
      const rowWarnings = warningsByFileRow.get(fileRow) ?? [];
      const fallbackCluster = unevaluatedCluster(reportState);
      const clusterId =
        errors.length > 0
          ? primaryErrorCluster(errors)
          : fallbackCluster !== null
            ? fallbackCluster
            : rowWarnings.length > 0
            ? warningCluster(rowWarnings)
            : "passed";
      return {
        rowIndex,
        fileRow,
        employeeCode:
          row?.employeeCode ?? errors[0]?.employeeCode ?? "Unknown employee",
        path: pathLabel(row),
        row,
        clusterId,
        errors,
        warnings: rowWarnings,
        issueResolution: classifySplitCostingIssueResolution(errors, row),
      };
    });
}

/** Stable presentation order: errors, warnings, then passed rows. */
export function groupSplitCostingRows(
  rows: readonly ClusteredSplitCostingRow[]
): SplitCostingClusterGroup[] {
  const byCluster = new Map<
    SplitCostingClusterId,
    ClusteredSplitCostingRow[]
  >();
  for (const row of rows) {
    const bucket = byCluster.get(row.clusterId);
    if (bucket) bucket.push(row);
    else byCluster.set(row.clusterId, [row]);
  }

  return [...ERROR_ORDER, ...WARNING_ORDER, ...STATUS_ORDER].flatMap(
    (id) => {
      const clusterRows = byCluster.get(id);
      return clusterRows
        ? [{ config: SPLIT_COSTING_CLUSTER_CONFIGS[id], rows: clusterRows }]
        : [];
    }
  );
}

export function countSplitCostingTabs(
  rows: readonly ClusteredSplitCostingRow[]
): SplitCostingTabCounts {
  let errors = 0;
  let warnings = 0;
  let passed = 0;
  for (const row of rows) {
    const kind = SPLIT_COSTING_CLUSTER_CONFIGS[row.clusterId].kind;
    if (kind === "error") errors += 1;
    else if (kind === "warning") warnings += 1;
    else if (kind === "ready" || kind === "passed") passed += 1;
  }
  return { all: rows.length, errors, warnings, passed };
}

export function visibleSplitCostingGroups(
  tab: SplitCostingReportTab,
  groups: readonly SplitCostingClusterGroup[]
): SplitCostingClusterGroup[] {
  if (tab === "all") return [...groups];
  const kind = tab === "errors" ? "error" : tab === "warnings" ? "warning" : "passed";
  return groups.filter(
    (group) =>
      group.config.kind === kind ||
      (tab === "passed" && group.config.kind === "ready")
  );
}

/** Case-insensitive employee-code substring match used by report search. */
export function matchesSplitCostingEmployeeCodeSearch(
  employeeCode: string,
  term: string
): boolean {
  const trimmed = term.trim();
  if (trimmed === "") return true;
  return employeeCode.toLowerCase().includes(trimmed.toLowerCase());
}

/**
 * Filters rows within existing groups and drops empty groups without changing
 * cluster order or the source groups used for chips and tab counts.
 */
export function filterSplitCostingGroupsByEmployeeCode(
  groups: readonly SplitCostingClusterGroup[],
  term: string
): SplitCostingClusterGroup[] {
  if (term.trim() === "") return [...groups];
  return groups
    .map((group) => ({
      ...group,
      rows: group.rows.filter((row) =>
        matchesSplitCostingEmployeeCodeSearch(row.employeeCode, term)
      ),
    }))
    .filter((group) => group.rows.length > 0);
}

export function prerequisiteSplitCostingErrors(
  result: SplitCostingImportWorkflowResult
): SplitCostingImportError[] {
  return result.errors.filter((error) => error.index === null);
}

export function formatSplitCostingBlockedBanner(
  errorCount: number,
  totalCount: number
): string {
  return `${errorCount} of ${totalCount} row${totalCount === 1 ? "" : "s"} ${
    errorCount === 1 ? "has" : "have"
  } a blocking error. Nothing will be committed until every blocking error is fixed.`;
}
